import React, { useCallback, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import MyAppText from '../../components/MyAppText';
import { translate } from '../../services/translate';
import { dashboardSummaryService, GetOccurrencesSummary } from '../../services/central-api/dashboard-summary';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import '../../styles/date-ranger-picker.css';
import HeatMap from '../../components/HeatMap';
import { DashBoardParamList } from '../../typings/Params';
import useFilters from '../../components/Filter/hooks/useFilters';
import OccurrencesFilter from '../dispatch/Occurrences/Filters';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Container from '../../components/Container';
import Header from '../../components/Header';

export default function DashboardOccurrences() {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DashBoardParamList, 'DashboardOccurrences'>>();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [summary, setSummary] = useState<GetOccurrencesSummary>({
        total: 0,
        pendingOccurrences: 0,
        inServiceOccurrences: 0,
        onGoingOccurrences: 0,
        concludedOccurrences: 0,
        onFootTeamsTotal: 0,
        fourWheelsTeamsTotal: 0,
        twoWheelsTeamsTotal: 0,
        activesTotal: 0,
        onFootTeamsCurrent: 0,
        fourWheelsTeamsCurrent: 0,
        twoWheelsTeamsCurrent: 0,
        onFootTeamsNameTotal: 0,
        fourWheelsTeamsNameTotal: 0,
        twoWheelsTeamsNameTotal: 0,
        activesCurrent: 0,
        posList: []
    });

    const [filters, setFilters] = useFilters<DashBoardParamList['DashboardOccurrences']>({ ...route.params });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    async function getOccurrencesSummary() {
        try {
            setIsLoading(true);
            const occurrencesSummary = await dashboardSummaryService.getOccurrencesSummary(filters);
            setSummary(occurrencesSummary);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getOccurrencesSummary();
        }
    }, [isFocused, filters]));

    return <Container>
        <Header
            title={translate('occurrences')}
            actions={<OccurrencesFilter
                filters={filters}
                setFilters={setFilters}
                textFilter={textFilter}
                setTextFilter={setTextFilter}
            />}
        />
        <View style={styles.container}>
            {isLoading ?
                <ActivityIndicator size='large' style={{ minHeight: 190 }} color={theme.colors.iconColor} /> :
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 20 }}>
                    <View style={{ flex: 2, flexDirection: 'row', justifyContent: 'space-between', minWidth: 250 }}>
                        <View style={styles.row}>
                            <View>
                                <View style={styles.textTitle}>
                                    <MyAppText style={styles.text}>{translate('2WheelsTeams')}:</MyAppText>
                                </View>
                                <MyAppText style={styles.number}>{summary.twoWheelsTeamsCurrent}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('activeTeams')}
                                </MyAppText>
                                <MyAppText style={styles.number}>{summary.twoWheelsTeamsTotal}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('teamsThatShouldBeActive')}
                                </MyAppText>
                                <MyAppText style={styles.number}>{summary.twoWheelsTeamsNameTotal}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('availableTeams')}
                                </MyAppText>
                            </View>
                            <View >
                                <View style={styles.textTitle}>
                                    <MyAppText style={styles.text}>{translate('4WheelsTeams')}:</MyAppText>
                                </View>
                                <MyAppText style={styles.number}>{summary.fourWheelsTeamsCurrent}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('activeTeams')}
                                </MyAppText>
                                <MyAppText style={styles.number}>{summary.fourWheelsTeamsTotal}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('teamsThatShouldBeActive')}
                                </MyAppText>
                                <MyAppText style={styles.number}>{summary.fourWheelsTeamsNameTotal}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('availableTeams')}
                                </MyAppText>
                            </View>
                            <View>
                                <View style={styles.textTitle}>
                                    <MyAppText style={styles.text}>{translate('OnFootTeams')}:</MyAppText>
                                </View>
                                <MyAppText style={styles.number}>{summary.onFootTeamsCurrent}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('activeTeams')}
                                </MyAppText>
                                <MyAppText style={styles.number}>{summary.onFootTeamsTotal}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('teamsThatShouldBeActive')}
                                </MyAppText>
                                <MyAppText style={styles.number}>{summary.onFootTeamsNameTotal}</MyAppText>
                                <MyAppText style={styles.subText}>
                                    {translate('availableTeams')}
                                </MyAppText>
                            </View>
                        </View>
                    </View>
                    <View style={styles.cardList}>
                        <View style={styles.occurrenceTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('type')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('amount')}</MyAppText>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.occurrenceTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('pending')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.pendingOccurrences}</MyAppText>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.occurrenceTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('in_service')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.inServiceOccurrences}</MyAppText>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.occurrenceTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('status_on_going')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.onGoingOccurrences}</MyAppText>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.occurrenceTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('concluded')}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.concludedOccurrences}</MyAppText>
                            </View>
                        </View>
                    </View>
                </View>
            }
            <HeatMap posList={summary.posList} />
        </View>
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        rowGap: 15,
    },
    cardList: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        backgroundColor: theme.colors.container.background,
        flex: 1,
        padding: 20,
        flexGrow: 3,
        flexDirection: 'column',
        justifyContent: 'space-evenly',
    },
    tableLine: {
        borderBottomColor: theme.colors.container.border,
        borderBottomWidth: 1,
    },
    tableRows: {
        flex: 1,
        justifyContent: 'space-evenly',
    },
    occurrenceTableRow: {
        flexDirection: 'row',
        flex: 1
    },
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    textTitle: {
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'center'
    },
    text: {
        fontSize: 15,
        color: theme.colors.labelColor,
        whiteSpace: 'break-spaces'
    },
    subText: {
        fontSize: 12,
        color: theme.colors.labelColor,
        whiteSpace: 'break-spaces',
        alignSelf: 'center',
    },
    tableText: {
        color: theme.colors.labelColor,
        margin: 5,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    number: {
        fontSize: 35,
        fontWeight: 'bold',
        alignSelf: 'center',
    },
}));
